import {
  Autocomplete,
  Box,
  CircularProgress,
  Divider,
  InputAdornment,
  TextField,
  TextFieldProps,
} from '@mui/material'
import CustomLabel from 'components/common/CustomLabel/CustomLabel'
import CustomText from 'components/common/CustomText/CustomText'
import { ReactNode } from 'react'
import { Control, Controller, FieldError } from 'react-hook-form'
import { colors } from 'styles/colors'
import { customTextFieldSlotProps, customTextFieldSx } from 'styles/customTextFieldStyles'
import useGlobalStyles from 'styles/globalStyles'

export type CustomFormAutoCompleteProps<T> = {
  name: string
  control: Control<any>
  options: T[]
  getOptionLabel: (option: T) => string
  isOptionEqualToValue?: (option: T, value: T) => boolean
  error?: FieldError
  placeholder?: string
  label?: string
  required?: boolean
  note?: string
  floatingLabel?: string
  startIcon?: ReactNode
  endIcon?: ReactNode
  onOptionChange?: (value: T | null) => void
  onInputChange?: (
    event: React.SyntheticEvent,
    value: string
  ) => void,
  loading: boolean,
} & Omit<TextFieldProps, 'name' | 'error' | 'helperText' | 'onChange'>

export default function CustomFormAutoComplete<T>({
  name,
  control,
  options,
  getOptionLabel,
  isOptionEqualToValue,
  error,
  placeholder,
  label,
  required,
  note,
  sx,
  floatingLabel,
  startIcon,
  endIcon,
  onOptionChange,
  onInputChange,
  loading,
  ...rest
}: CustomFormAutoCompleteProps<T>) {
  const globalStyles = useGlobalStyles()

  return (
    <Box>
      {label && (
        <CustomLabel
          label={label}
          note={note}
          required={required}
        />
      )}

      <Controller
        name={name}
        control={control}
        render={({ field }) => (
          <Autocomplete
            options={options}
            getOptionLabel={getOptionLabel}
            isOptionEqualToValue={isOptionEqualToValue}
            value={field.value ?? null}
            onChange={(_, value) => {
              field.onChange(value)
              onOptionChange?.(value)
            }}
            onInputChange={(event, value) => {
              onInputChange?.(event, value)
            }}
            loading={loading}
            loadingText={
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, p: 1 }}>
                <CircularProgress size={16} sx={{ color: colors.gray }} />
              </Box>
            }
            renderOption={(props, option, { index }) => (
              <Box component="li" {...props} px={1} key={index}>
                <CustomText text={getOptionLabel(option)} color={colors.black} />
                <Box my={1}>
                  <Divider />
                </Box>
              </Box>
            )}
            fullWidth
            renderInput={(params) => (
              <TextField
                {...params}
                {...rest}
                placeholder={placeholder}
                size="small"
                label={floatingLabel}
                error={!!error}
                helperText={error?.message}
                slotProps={{
                  ...customTextFieldSlotProps,
                  input: {
                    ...params.InputProps,
                    notched: true,
                    startAdornment: startIcon ? (
                      <InputAdornment position="start">
                        {startIcon}
                      </InputAdornment>
                    ) : params.InputProps.startAdornment,
                    endAdornment: (
                      <>
                        {endIcon && (
                          <InputAdornment position="end">
                            {endIcon}
                          </InputAdornment>
                        )}
                        {params.InputProps.endAdornment}
                      </>
                    ),
                  },
                  inputLabel: {
                    shrink: true,
                    required,
                    className: globalStyles.floatingLabelText,
                  },
                }}
                sx={{
                  ...(startIcon && {
                    '& .MuiOutlinedInput-root.MuiInputBase-adornedStart .MuiOutlinedInput-input': {
                      paddingLeft: 0.5,
                    },
                  }),
                  ...(endIcon && {
                    '& .MuiOutlinedInput-root.MuiInputBase-adornedEnd': {
                      paddingRight: 1,
                    },
                  }),
                  ...customTextFieldSx,
                  ...sx,
                }}
              />
            )}
          />
        )}
      />
    </Box>
  )
}
